import { motion } from "framer-motion";
import { BatteryCharging, CloudRain, Moon, Sun, Wrench, ShieldCheck } from "lucide-react";

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

const SpecCell = ({ icon: Icon, value, label, className = "", delay = 0, testId }) => (
  <motion.div
    {...reveal(delay)}
    className={`border border-white/10 rounded-2xl p-8 sm:p-10 bg-stone-900/40 hover:border-amber-400/40 transition-colors duration-500 group ${className}`}
    data-testid={testId}
  >
    <Icon className="w-6 h-6 text-amber-400 mb-8 transition-transform duration-500 group-hover:scale-110" />
    <div className="font-serif text-4xl sm:text-5xl text-stone-50 tracking-tight">{value}</div>
    <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mt-3">{label}</div>
  </motion.div>
);

export const Features = () => (
  <section id="features" className="py-32 sm:py-40" data-testid="features-section">
    <div className="max-w-7xl mx-auto px-6 sm:px-10">
      <motion.p {...reveal()} className="text-xs uppercase tracking-[0.3em] text-amber-400 mb-6">
        Everything on board
      </motion.p>
      <motion.h2
        {...reveal(0.1)}
        className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-stone-50 mb-20 max-w-2xl"
      >
        Small light. <span className="italic text-amber-400">Serious</span> engineering.
      </motion.h2>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <motion.div
          {...reveal(0.1)}
          className="md:col-span-7 relative overflow-hidden rounded-2xl aspect-[16/10] clip-frame group"
          data-testid="feature-closeup-image"
        >
          <img
            src="/images/closeup.png"
            alt="Close-up of the LumaDeck warm LED dome"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute bottom-6 left-6 backdrop-blur-xl bg-stone-950/60 border border-white/10 rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-stone-200">
            2700K warm white dome
          </div>
        </motion.div>

        <SpecCell icon={Sun} value="6–8h" label="sunlight to full charge" className="md:col-span-5" delay={0.2} testId="spec-charge" />
        <SpecCell icon={BatteryCharging} value="600 mAh" label="rechargeable battery" className="md:col-span-4" delay={0.1} testId="spec-battery" />
        <SpecCell icon={Moon} value="8–10h" label="glow every night" className="md:col-span-4" delay={0.2} testId="spec-runtime" />
        <SpecCell icon={CloudRain} value="IP65" label="rain & dust proof" className="md:col-span-4" delay={0.3} testId="spec-ip65" />

        <motion.div
          {...reveal(0.15)}
          className="md:col-span-7 relative overflow-hidden rounded-2xl aspect-[16/9] clip-frame group"
          data-testid="feature-stairs-image"
        >
          <img
            src="/images/stairs.png"
            alt="LumaDeck lights guiding outdoor steps at night"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </motion.div>

        <div className="md:col-span-5 flex flex-col gap-6">
          <SpecCell icon={Wrench} value="2 ways" label="screw or adhesive mount" delay={0.2} testId="spec-mount" />
          <SpecCell icon={ShieldCheck} value="1 year" label="warranty on every pack" delay={0.3} testId="spec-warranty" />
        </div>
      </div>
    </div>
  </section>
);
